// src/services/staffConfirmation/valueMovingConfirmation.ts - the staff
// confirmation for office actions that move value and asked for nothing before
// (deposit create, approve and reject, the approvals checker, charge repair,
// refund check, referral funding and release).
//
//
// These screens have no code field, so they reuse the one prompt host: the
// request is always marked `stepUp: "host"`. When the api asks for a staff
// confirmation and "Confirm with Google" is on (both launch controls allow it,
// see staffConfirmationControls, or the api has just asked for one), the host
// shows "Confirm with Google" and re-sends the identical request once with
// `X-Staff-Confirmation`, bound to that exact request by the transport
// (bindStaffConfirmation).
//
// The mark is not decided from the gate read. The gate is read once at sign-in
// and a failed read means OFF, so the api can ask while the office reads OFF; a
// request marked only while the gate read ON would then show the api's refusal
// as an error, and only the next press would ask. Always marked, the first
// refusal arms the prompt (noteConfirmationDemanded) and the host answers it.
//
// The mark rides on the axios config, never the wire: a request the api accepts
// sends the same method, path, body and headers with the controls ON or OFF.
// With no issuer configured there is no Google, and a staff-confirmation refusal
// stays the screen's own error.
//
// Ledger re-drive and settlement cancel also always send `stepUp: "host"`,
// settlement cancel has a required reason, and chain changes go through
// ChainChangeReview's useStepUp. They only use the settled check below.
//
// The route inventory and the rule that keeps it complete live in
// src/__tests__/architecture/valueMovingRoutes.test.ts.
import { fetcher } from "../../hooks/fetcher";
import type { StepUpHandling } from "../stepUp";
import { isSettledByStepUpPrompt } from "../twoFactorErrors";
import { isStaffConfirmationError } from "./staffConfirmationErrors";

/** The request options a confirmed value-moving request carries. */
export interface StaffConfirmationHostOptions {
  stepUp: StepUpHandling;
  /** What the operator is doing, for the prompt's copy. */
  stepUpAction: string;
}

/**
 * The prompt-host opt-in for a value-moving request whose screen has no code
 * field. Always given, whatever the gate read said: it never reaches the wire,
 * and the host only answers a refusal (see the file comment).
 *
 * COPY: `stepUpAction` is staff-facing copy, pending the CTO's approval.
 */
export const staffConfirmationHost = (
  stepUpAction: string
): StaffConfirmationHostOptions => ({ stepUp: "host", stepUpAction });

let refreshAfterLostAnswer: () => void = () => undefined;

/**
 * What a screen with no refresh of its own runs after a lost answer: the app
 * re-reads what is on show, so no list keeps a stale row for a request that may
 * have landed.
 */
export const setLostAnswerRefresh = (refresh: () => void): void => {
  refreshAfterLostAnswer = refresh;
};

/**
 * The confirmation prompt already said everything about this failure: it
 * closed ("Not now", a stop after a second refusal, a lost answer), or the
 * office refused to send the request itself (a spent or abandoned
 * confirmation). A screen that toasts its errors shows nothing more for these.
 * `refresh` runs when a lost answer means the request may have landed.
 *
 * Deliberately NOT every staff-confirmation code from the api: a code such as
 * RATE_LIMITED or SESSION_ENDED on a request no prompt handled has not been
 * shown to anyone, so the screen must still say it failed.
 *
 * With the controls OFF this is reached only once the api has asked for a
 * confirmation (the prompt then answered it), or for the step-up prompt every
 * host request already had.
 */
export const isSettledByStaffConfirmation = (
  error: unknown,
  refresh: () => void = refreshAfterLostAnswer
): boolean =>
  isSettledByStepUpPrompt(error, refresh) || isStaffConfirmationError(error);

/**
 * `fetcher` for a value-moving request from a screen with no code field, opted
 * into the prompt host (staffConfirmationHost). On the wire it is EXACTLY
 * `fetcher(url, method, body)`, or with `headers` when the caller sends some (an
 * Idempotency-Key, which the host's re-send carries unchanged).
 *
 * The architecture test reads this as a request helper (testing/adminRequestScan).
 */
export const fetchWithStaffConfirmation = <TResponse, TRequest = undefined>(
  url: string,
  method: string,
  body: TRequest,
  stepUpAction: string,
  headers?: Record<string, string>
): Promise<TResponse> =>
  fetcher<TResponse, TRequest>(
    url,
    method,
    body,
    headers,
    staffConfirmationHost(stepUpAction)
  );
